import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import "../assets/css/Projects.css";

const PAYMENT_TYPES = ["Advance", "Final", "Partial"];

function EditProjectPayment({ onBack, paymentId: paymentIdProp }) {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const paymentId = paymentIdProp ?? idParam;

  const role = (() => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  })();

  const canEdit = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [customerId, setCustomerId] = useState(null);

  const [payment, setPayment] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(true);

  const [formData, setFormData] = useState({
    paymentDate: "",
    currencyId: "",
    amount: "",
    paymentModeId: "",
    transactionRef: "",
    totalAmount: "",
    paymentType: "Advance",
    remarks: "",
  });

  const paymentTypes = useMemo(() => PAYMENT_TYPES, []);

  const getCreatedBy = () => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return 1;
      const user = JSON.parse(u);
      return user.user_id ?? user.id ?? 1;
    } catch {
      return 1;
    }
  };

  useEffect(() => {
    const fetchLookups = async () => {
      setLoadingLookups(true);
      try {
        const [currRes, modeRes] = await Promise.all([
          axiosInstance.get("/currencies").catch(() => ({ data: [] })),
          axiosInstance.get("/payment-modes").catch(() => ({ data: [] })),
        ]);

        const rawCurr = Array.isArray(currRes.data)
          ? currRes.data
          : currRes.data?.data ?? currRes.data?.currencies ?? [];
        const fallbackList = [
          { id: 1, name: "Indian Rupee", code: "INR", symbol: "₹" },
          { id: 2, name: "US Dollar", code: "USD", symbol: "$" },
        ];

        const currList =
          rawCurr.length > 0
            ? rawCurr.map((c) => ({
                id: c.Currency_Id ?? c.currency_id ?? c.id,
                name: c.Currency_Name ?? c.currency_name ?? c.name ?? "",
                code: c.Currency_Code ?? c.currency_code ?? c.code ?? "",
                symbol: c.Currency_Symbol ?? c.currency_symbol ?? c.symbol ?? "",
              }))
            : fallbackList;

        const filtered = currList.filter((c) => {
          const code = String(c?.code ?? "").trim().toUpperCase();
          const symbol = String(c?.symbol ?? "").trim();
          const isInr = code === "INR" || symbol === "₹";
          const isUsd = code === "USD" || (code === "" && symbol === "$");
          return isInr || isUsd;
        });

        setCurrencies(filtered.length > 0 ? filtered : fallbackList);

        const rawModes = Array.isArray(modeRes.data)
          ? modeRes.data
          : modeRes.data?.data ?? modeRes.data?.payment_modes ?? [];
        const modeList =
          rawModes.length > 0
            ? rawModes.map((m) => ({
                id: m.Payment_Mode_Id ?? m.payment_mode_id ?? m.id,
                name: m.Payment_Mode_Name ?? m.payment_mode_name ?? m.name ?? "",
              }))
            : [
                { id: 1, name: "Cash" },
                { id: 2, name: "Bank Transfer" },
                { id: 3, name: "UPI" },
                { id: 4, name: "Cheque" },
              ];
        setPaymentModes(modeList);
      } catch (err) {
        console.error("Error fetching payment lookups:", err);
      } finally {
        setLoadingLookups(false);
      }
    };

    fetchLookups();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!paymentId) return;
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/project-payments/${paymentId}`);
        const p = res.data?.data ?? res.data?.payment ?? res.data ?? null;
        setPayment(p);

        const projId = p?.Project_Id ?? p?.project_id ?? null;
        const custId = p?.client_id ?? p?.client_id ?? null;
        setProjectId(projId);
        setCustomerId(custId);

        setFormData({
          paymentDate: p?.Payment_Date ?? p?.payment_date ?? "",
          currencyId: p?.Currency_Id ?? p?.currency_id ?? "",
          amount: p?.Amount ?? p?.amount ?? "",
          paymentModeId: p?.Payment_Mode_Id ?? p?.payment_mode_id ?? "",
          transactionRef: p?.Transaction_Ref ?? p?.transaction_ref ?? "",
          totalAmount: p?.Total_Amount ?? p?.total_amount ?? "",
          paymentType: p?.Payment_Type ?? p?.payment_type ?? "Advance",
          remarks: p?.Remarks ?? p?.remarks ?? "",
        });
      } catch (err) {
        console.error("Error loading project payment:", err);
        alert("Failed to load payment details.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [paymentId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paymentId) return;
    if (!canEdit) {
      alert("You don't have permission to edit project payments.");
      return;
    }
    if (!projectId || !customerId) {
      alert("Project and Customer are required.");
      return;
    }
    if (!formData.paymentDate?.trim()) {
      alert("Payment Date is required.");
      return;
    }

    const amountNum = parseFloat(String(formData.amount).replace(/,/g, ""));
    const totalNum = parseFloat(String(formData.totalAmount).replace(/,/g, ""));
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      alert("Please enter a valid amount.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        Project_Id: Number(projectId),
        client_id: Number(customerId),
        Payment_Date: formData.paymentDate,
        Currency_Id: formData.currencyId ? Number(formData.currencyId) : null,
        Amount: amountNum,
        Payment_Mode_Id: formData.paymentModeId ? Number(formData.paymentModeId) : null,
        Transaction_Ref: formData.transactionRef?.trim() || null,
        Total_Amount: Number.isNaN(totalNum) ? null : totalNum,
        Payment_Type: formData.paymentType,
        Remarks: formData.remarks?.trim() || null,
        Updated_By: getCreatedBy(),
      };

      const res = await axiosInstance.put(`/update-project-payment/${paymentId}`, payload);
      alert(res?.data?.message || "Payment updated successfully.");

      const refresh = Date.now();
      navigate(`/projects/${projectId}/view?refresh=${refresh}`);
    } catch (err) {
      console.error("Error updating project payment:", err);
      alert(err.response?.data?.message || err.response?.data?.error || err.message || "Failed to update payment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div>Loading payment details...</div>
      </div>
    );
  }

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button className="viewlead-back-link" type="button" onClick={onBack || (() => navigate(-1))}>
          <FaArrowLeft /> Back
        </button>
      </div>

      <div className="add-project-payment-form">
        <h4 className="viewlead-section-title" style={{ marginBottom: 16 }}>
          Update Payment
        </h4>
        <form onSubmit={handleSubmit} className="project-payment-form-grid">
          <div className="form-group">
            <label>
              Payment Date <span className="required">*</span>
            </label>
            <input type="date" name="paymentDate" value={formData.paymentDate} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Currency</label>
            <select name="currencyId" value={formData.currencyId} onChange={handleChange} disabled={loadingLookups}>
              <option value="">{loadingLookups ? "Loading..." : "Select Currency"}</option>
              {currencies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.symbol || c.code || c.name || c.id}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>
              Amount <span className="required">*</span>
            </label>
            <input type="text" name="amount" value={formData.amount} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Payment Mode</label>
            <select
              name="paymentModeId"
              value={formData.paymentModeId}
              onChange={handleChange}
              disabled={loadingLookups}
            >
              <option value="">{loadingLookups ? "Loading..." : "Select Mode"}</option>
              {paymentModes.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Transaction Ref</label>
            <input type="text" name="transactionRef" value={formData.transactionRef} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Total Amount</label>
            <input type="text" name="totalAmount" value={formData.totalAmount} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Payment Type</label>
            <select name="paymentType" value={formData.paymentType} onChange={handleChange}>
              {paymentTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group form-group-full">
            <label>Remarks</label>
            <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows={2} />
          </div>
          <div className="form-group form-group-actions">
            <button type="submit" className="btn-subproject-primary" disabled={submitting}>
              {submitting ? "Updating..." : "Update Payment"}
            </button>
            <button type="button" className="btn-secondary" disabled={submitting} onClick={() => (onBack ? onBack() : navigate(-1))}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProjectPayment;

